import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './asc-badge.styles';
import { dispatch } from '../../utils/event-helpers';

type BadgeSize = 'sm' | 'md' | 'lg';
type BadgeVariant = 'pill' | 'badge';
type BadgeTheme = 'success' | 'gray' | 'brand' | 'error';

/**
 * A compact inline labeling element used to convey status, categorization,
 * or metadata at a glance.
 *
 * Supports two shape variants (`pill` / `badge`), four color themes, three
 * sizes, and optional leading/trailing affordances (icon, avatar, dot indicator,
 * close "×"). An `icon-only` mode renders only the leading slot content while
 * retaining the default slot text as screen-reader-only accessible text.
 *
 * Consumers who need keyboard-accessible trailing affordances (e.g. a close
 * button) must slot a native `<button>` into the `trailing` slot. The badge
 * itself has no built-in keyboard interaction.
 *
 * @element asc-badge
 *
 * @fires asc-click - Fired on any click of the badge. No detail.
 *
 * @slot - Required. Primary label text. Visually hidden in `icon-only` mode;
 *   the text is captured and used as `aria-label` on the shadow base element.
 * @slot leading - Optional. One icon, avatar, or dot indicator rendered left of
 *   the label. When both `leading` and `trailing` are populated the `trailing`
 *   slot is suppressed and a warning is emitted.
 * @slot trailing - Optional. One icon or close affordance rendered right of the
 *   label. Suppressed in `icon-only` mode and when `leading` is also populated.
 *
 * @csspart base     - Root `<span>` wrapper — layout, background, border, shape.
 * @csspart leading  - `<span>` wrapping the leading slot.
 * @csspart label    - `<span>` wrapping the default slot.
 * @csspart trailing - `<span>` wrapping the trailing slot.
 *
 * @cssprop --asc-badge-padding-x          - Horizontal padding; applied per size.
 * @cssprop --asc-badge-padding-y          - Vertical padding; applied per size.
 * @cssprop --asc-badge-font-size          - Label font size; applied per size.
 * @cssprop --asc-badge-font-weight        - Label font weight. Default: `var(--asc-font-weight-medium)`
 * @cssprop --asc-badge-line-height        - Label line height; applied per size.
 * @cssprop --asc-badge-gap               - Gap between leading, label, trailing; applied per size.
 * @cssprop --asc-badge-icon-size          - Icon dimensions in leading/trailing slots; applied per size.
 * @cssprop --asc-badge-avatar-size        - Avatar diameter in the leading slot; applied per size.
 * @cssprop --asc-badge-border-radius      - Border radius; applied per variant.
 * @cssprop --asc-badge-bg                 - Background color; applied per theme.
 * @cssprop --asc-badge-border-color       - Border color; applied per theme.
 * @cssprop --asc-badge-border-width       - Border width. Default: `var(--asc-border-width)`
 * @cssprop --asc-badge-fg                 - Foreground text color; applied per theme.
 * @cssprop --asc-badge-icon-color         - Icon and dot indicator color; applied per theme. Resolved from `--asc-fg-*` tokens, which differ from text colors for brand and success themes.
 * @cssprop --asc-badge-avatar-ring-color  - Avatar ring/border color; applied per theme.
 */
@customElement('asc-badge')
export class AscBadge extends LitElement {
  static override styles = [styles];

  /** The overall size of the badge — affects padding, font size, gap, icon size, and avatar size. */
  @property({ type: String, reflect: true }) size: BadgeSize = 'md';

  /** Controls the border-radius shape. `pill` is fully rounded; `badge` is lightly rounded. */
  @property({ type: String, reflect: true }) variant: BadgeVariant = 'badge';

  /** The color palette applied to the badge surface, border, text, and icons. */
  @property({ type: String, reflect: true }) theme: BadgeTheme = 'gray';

  /**
   * When `true`, hides the default slot label visually and suppresses the
   * trailing slot. The default slot text is captured and set as `aria-label`
   * on the shadow base element. Requires a leading slot element.
   */
  @property({ type: Boolean, reflect: true, attribute: 'icon-only' }) iconOnly = false;

  @state() private _hasLeading = false;
  @state() private _hasTrailing = false;
  @state() private _labelText = '';

  private _hasSlotConflict = false;

  private _handleClick(): void {
    dispatch(this, 'asc-click');
  }

  private _updateSlotConflictWarning(): void {
    const hasConflict = this._hasLeading && this._hasTrailing;
    if (hasConflict && !this._hasSlotConflict) {
      console.warn(
        '[asc-badge] Both leading and trailing slots are populated — this combination is unsupported. The trailing slot will be suppressed.'
      );
    }
    this._hasSlotConflict = hasConflict;
  }

  private _onLeadingSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasLeading = slot.assignedNodes({ flatten: true }).length > 0;
    this._updateSlotConflictWarning();
  }

  private _onTrailingSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasTrailing = slot.assignedNodes({ flatten: true }).length > 0;
    this._updateSlotConflictWarning();
  }

  private _onLabelSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._labelText = slot
      .assignedNodes({ flatten: true })
      .map((n) => n.textContent ?? '')
      .join('')
      .trim();
  }

  override render() {
    const showTrailing = !this.iconOnly && this._hasTrailing && !this._hasLeading;

    return html`
      <span
        part="base"
        class="base"
        @click=${this._handleClick}
      >
        <span
          part="leading"
          class="leading"
          ?hidden=${!this._hasLeading}
        >
          <slot name="leading" @slotchange=${this._onLeadingSlotChange}></slot>
        </span>

        <span
          part="label"
          class="label ${this.iconOnly ? 'sr-only' : ''}"
          role=${this.iconOnly ? 'img' : nothing}
          aria-label=${this.iconOnly ? this._labelText : nothing}
        >
          <slot @slotchange=${this._onLabelSlotChange}></slot>
        </span>

        <span
          part="trailing"
          class="trailing"
          ?hidden=${!showTrailing}
        >
          <slot name="trailing" @slotchange=${this._onTrailingSlotChange}></slot>
        </span>
      </span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-badge': AscBadge;
  }
}
